import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ImageGallery = ({ images, onImagesChange, primaryImageIndex, onPrimaryImageChange }) => {
  const handleDeleteImage = (index) => {
    const newImages = images?.filter((_, i) => i !== index);
    onImagesChange(newImages);
    
    if (primaryImageIndex === index) {
      onPrimaryImageChange(newImages?.length > 0 ? 0 : null);
    } else if (primaryImageIndex > index) {
      onPrimaryImageChange(primaryImageIndex - 1);
    }
  };

  const handleSetPrimary = (index) => {
    onPrimaryImageChange(index);
  };

  const moveImage = (fromIndex, toIndex) => {
    const newImages = [...images];
    const [movedImage] = newImages?.splice(fromIndex, 1);
    newImages?.splice(toIndex, 0, movedImage);
    onImagesChange(newImages);
    
    if (primaryImageIndex === fromIndex) {
      onPrimaryImageChange(toIndex);
    } else if (primaryImageIndex === toIndex) {
      onPrimaryImageChange(fromIndex > toIndex ? primaryImageIndex + 1 : primaryImageIndex - 1);
    }
  };

  if (images?.length === 0) {
    return null;
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Image Gallery</h2>
        <span className="text-sm text-muted-foreground">
          {images?.length} image{images?.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {images?.map((image, index) => (
          <div
            key={index}
            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-smooth ${
              primaryImageIndex === index
                ? 'border-primary elevation-2' :'border-border hover:border-primary/50'
            }`}
          >
            <Image
              src={image?.url}
              alt={`Furniture photograph ${index + 1} showing captured item from warehouse inventory`}
              className="w-full h-full object-cover"
            />

            {primaryImageIndex === index && (
              <div className="absolute top-2 left-2 px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-md flex items-center gap-1">
                <Icon name="Star" size={12} />
                <span>Primary</span>
              </div>
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent opacity-0 hover:opacity-100 transition-smooth">
              <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  {index > 0 && (
                    <Button
                      variant="secondary"
                      size="icon"
                      onClick={() => moveImage(index, index - 1)}
                      iconName="ChevronLeft"
                      iconSize={16}
                      className="w-8 h-8"
                    >
                      <span className="sr-only">Move left</span>
                    </Button>
                  )}
                  {index < images?.length - 1 && (
                    <Button
                      variant="secondary"
                      size="icon"
                      onClick={() => moveImage(index, index + 1)}
                      iconName="ChevronRight"
                      iconSize={16}
                      className="w-8 h-8"
                    >
                      <span className="sr-only">Move right</span>
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {primaryImageIndex !== index && (
                    <Button
                      variant="secondary"
                      size="icon"
                      onClick={() => handleSetPrimary(index)}
                      iconName="Star"
                      iconSize={16}
                      className="w-8 h-8"
                    >
                      <span className="sr-only">Set as primary</span>
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => handleDeleteImage(index)}
                    iconName="Trash2"
                    iconSize={16}
                    className="w-8 h-8"
                  >
                    <span className="sr-only">Delete image</span>
                  </Button>
                </div>
              </div>
            </div>

            <div className="absolute top-2 right-2 px-2 py-1 bg-background/90 text-foreground text-xs font-medium rounded-md">
              {index + 1}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 p-4 bg-muted rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={18} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm text-foreground font-medium">Gallery Tips:</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Click star icon to set primary image</li>
              <li>• Use arrows to reorder images</li>
              <li>• Hover over images to see controls</li>
              <li>• Primary image appears first in client view</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageGallery;